import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export type BadgeTone =
  | "neutral"
  | "accent"
  | "success"
  | "warn"
  | "danger"
  | "machine";

const TONES: Record<BadgeTone, string> = {
  neutral: "bg-surface-2 text-muted border-border",
  accent: "bg-accent-soft text-accent border-accent/25",
  success: "bg-success-soft text-success border-success/25",
  warn: "bg-warn-soft text-warn border-warn/25",
  danger: "bg-danger-soft text-danger border-danger/25",
  machine: "bg-brand-pink text-on-brand border-brand-pink",
};

/**
 * A state chip. Bordered and soft-filled rather than solid: a table row can
 * carry three of these, and three solid blocks of colour in a row reads as
 * an alert rather than as state.
 */
export function Badge({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-chip border px-1.5 py-[1px]",
        "text-[11px] font-medium leading-[16px] whitespace-nowrap",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * The verdict chip, and the one place the console's colour semantics are
 * decided.
 *
 * `likely` is the machine pink. That is the whole reason pink is reserved
 * in this palette: `likely` means nobody has been there and confirmed it,
 * only that our pipeline believes it, and the app tells people so in those
 * words. An operator scanning a café list should be able to see which
 * verdicts are ours and which are somebody's, at a glance, without reading.
 */
export function VerdictBadge({
  verdict,
}: {
  verdict: "confirmed" | "likely" | "disputed" | "unknown" | null;
}) {
  if (verdict === null) return <Badge tone="neutral">no verdict</Badge>;
  switch (verdict) {
    case "confirmed":
      return <Badge tone="success">confirmed</Badge>;
    case "likely":
      return <Badge tone="machine">We think · nobody confirmed</Badge>;
    case "disputed":
      return <Badge tone="warn">disputed</Badge>;
    case "unknown":
      return <Badge tone="neutral">unknown</Badge>;
    default:
      // A verdict the spec has gained and this file has not. Render it
      // rather than nothing: an operator seeing an unfamiliar word is
      // better informed than one seeing a blank cell.
      return <Badge tone="neutral">{verdict}</Badge>;
  }
}

/** How old the evidence is. Muted on purpose — freshness qualifies a
 *  verdict, it does not compete with it. */
export function FreshnessBadge({
  freshness,
}: {
  freshness: "high" | "med" | "low" | "stale";
}) {
  const tone: BadgeTone =
    freshness === "high"
      ? "success"
      : freshness === "med"
        ? "neutral"
        : freshness === "low"
          ? "warn"
          : "danger";
  const label =
    freshness === "high"
      ? "fresh"
      : freshness === "med"
        ? "recent"
        : freshness === "low"
          ? "ageing"
          : "stale";
  return <Badge tone={tone}>{label}</Badge>;
}

/** Where a claim came from. Machine sources take the pink; a person's
 *  claim takes the accent, because a person is what the model is for. */
export function SourceBadge({ source }: { source: string }) {
  const machine =
    source === "ai_inferred" ||
    source === "ai_extracted" ||
    source === "operator_claimed";
  const label = source.replace(/_/g, " ").replace(/^ai /, "pipeline ");
  return (
    <Badge tone={machine ? "machine" : source === "admin" ? "warn" : "accent"}>
      {label}
    </Badge>
  );
}
